/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-05-18 09:47:45
 *
 *******************************************************/
// @flow
import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, Layout, Card, Stack, Select, TextStyle, Button } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import languageSetting from 'src/assets/images/language-setting/language-setting.png';
import {
  APP_LANGUAGES,
  APP_LANGUAGE_PSEUDO_LOCALIZATION_VALUE,
  APP_LANGUAGE_ENGLISH_VALUE
} from 'src/constants/language-setting';
import { isDevStore } from 'src/helpers/language-setting';
import * as oauthAction from 'actions/oauth-actions';

import MainMenu from 'src/components/containers/main-menu';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import InstallationRemindStatusBanner from 'src/components/containers/common/installation-remind-status-banner';
import { URL } from 'src/constants/links';
import TitleHelp from 'src/components/views/common/title-help';

type Props = {
  appLanguage: string,
  tenantId: string,
  loading: boolean,
  updateCurrentAppLanguage: (value: string) => void
};

type ComposedProps = Props & WithI18nProps;

type State = {
  selectValue: string
};

export class LanguageSetting extends Component<ComposedProps, State> {
  state = {
    selectValue: APP_LANGUAGE_ENGLISH_VALUE
  };

  componentDidMount() {
    const { appLanguage } = this.props;

    if (appLanguage) {
      this.setState({
        selectValue: appLanguage
      });
    }
  }

  render() {
    const { i18n, tenantId, loading } = this.props;
    const { selectValue } = this.state;

    let options = APP_LANGUAGES.map(function (language) {
      return {
        ...language,
        label: i18n.translate(language.label)
      };
    });

    if (!isDevStore(tenantId)) {
      options = options.filter(function ({ value }) {
        return value !== APP_LANGUAGE_PSEUDO_LOCALIZATION_VALUE;
      });
    }

    return (
      <div className="BC-Page BC-Page__Language-Setting">
        <MainMenu menuId="menu-account-app-language" />
        <Page title={i18n.translate('AdminHTML.LanguageSetting.Common.languageSetting')}>
          <TitleHelp i18n={i18n} hrefUserManual={URL['Link_V2_Tool_LanguageSetting_ManualGuide']} />
          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                {/* Installation reminder status banner */}
                <InstallationRemindStatusBanner />

                {/* Sticky notification for exceeded plan */}
                <ExceededPlanNotification />
              </div>
            </Layout.Section>
            <Layout.Section>
              <div className="BC-Language-Setting__Content">
                <Card title={i18n.translate('AdminHTML.LanguageSetting.LanguageSetting.cardTitle')}>
                  <Card.Section>
                    <Layout>
                      <Layout.Section>
                        <Stack vertical spacing="loose">
                          <div className="language-setting-select">
                            <Select
                              options={options}
                              onChange={this.handleSelectAppLanguage}
                              value={selectValue}
                              disabled={loading}
                            />
                          </div>
                          <TextStyle>
                            {i18n.translate('AdminHTML.LanguageSetting.LanguageSetting.textStyle', {
                              nt_new_line_character: <br />
                            })}
                          </TextStyle>
                          <Button primary onClick={this.handleChangeAppLanguage} loading={loading}>
                            {i18n.translate('AdminHTML.LanguageSetting.LanguageSetting.button')}
                          </Button>
                        </Stack>
                      </Layout.Section>
                      <Layout.Section secondary>
                        <div className="BC-Illustration-Image__Dashboard">
                          <img
                            src={languageSetting}
                            alt={i18n.translate('AdminHTML.LanguageSetting.Common.languageSetting')}
                          />
                        </div>
                      </Layout.Section>
                    </Layout>
                  </Card.Section>
                </Card>
              </div>
            </Layout.Section>
          </Layout>
        </Page>
      </div>
    );
  }

  handleSelectAppLanguage = (value: string) => {
    this.setState({
      selectValue: value
    });
  };

  handleChangeAppLanguage = () => {
    const { updateCurrentAppLanguage } = this.props;
    const { selectValue } = this.state;

    updateCurrentAppLanguage(selectValue);
  };
}

const mapStateToProps = state => ({
  appLanguage: _.get(state.oauth.tenantApp, 'appLanguage', APP_LANGUAGE_ENGLISH_VALUE),
  tenantId: _.get(state.oauth.tenantApp, 'tenantId', ''),
  loading: _.get(state.oauth, 'loading', false)
});

const mapDispatchToProps = (dispatch, props) => ({
  updateCurrentAppLanguage: appLanguage =>
    dispatch(oauthAction.updateCurrentAppLanguage({ appLanguage }, props.i18n))
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(LanguageSetting));
